import RiCloseLine from '@ben/icons/close-line';
import { Accordion } from '@ben/orion/accordion';
import IconButton from '@ben/orion/icon-button';
import IconSelect from '@ben/orion/icon-select';
import Input, { InputVariant } from '@ben/orion/input';
import RIcon from '@ben/orion/remix-icon';
import Select from '@ben/orion/select';
import TruncatedText from '@ben/orion/truncated-text';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import { Grid } from '@mantine/core';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

export interface Feature {
  id?: number;
  title: string;
  description: string;
  icon?: string;
  category: string;
  rank?: number;
}

export enum FeatureCategory {
  Lifestyle = 'BENEFIT_TYPE_LIFESTYLE',
  Finance = 'BENEFIT_TYPE_FINANCE',
  Family = 'BENEFIT_TYPE_FAMILY',
  HealthWellbeing = 'BENEFIT_TYPE_HEALTH_WELLBEING',
  Other = 'BENEFIT_TYPE_OTHER',
}

interface FeatureItemProps {
  feature: Feature;
  index: number;
  onChangeFeature: (feature: Feature) => void;
  onRemoveFeature: () => void;
  showRankField?: boolean;
}

const FeatureItem = ({
  feature,
  index,
  onChangeFeature,
  onRemoveFeature,
  showRankField = false,
}: FeatureItemProps) => {
  const { t } = useTranslation();
  const { icon, title, description, category, rank } = feature;

  const handleFieldChange = (field: string, value?: string) => {
    if (field === 'rank') {
      onChangeFeature({ ...feature, [field]: Number(value) });
      return;
    }
    onChangeFeature({ ...feature, [field]: value });
  };

  const featureCategoryOptions = useMemo(() => {
    return [
      {
        label: t('manageBenefitFeatures.category.lifestyle', {
          defaultValue: 'Lifestyle',
        }),
        value: FeatureCategory.Lifestyle,
      },
      {
        label: t('manageBenefitFeatures.category.finance', {
          defaultValue: 'Finance',
        }),
        value: FeatureCategory.Finance,
      },
      {
        label: t('manageBenefitFeatures.category.family', {
          defaultValue: 'Family',
        }),
        value: FeatureCategory.Family,
      },
      {
        label: t('manageBenefitFeatures.category.healthWellbeing', {
          defaultValue: 'Health & wellbeing',
        }),
        value: FeatureCategory.HealthWellbeing,
      },
      {
        label: t('manageBenefitFeatures.category.other', {
          defaultValue: 'Other',
        }),
        value: FeatureCategory.Other,
      },
    ];
  }, [t]);

  return (
    <Accordion.Item value={String(index)}>
      <Accordion.Control
        sideContent={
          <IconButton icon={RiCloseLine} onClick={onRemoveFeature} />
        }
      >
        <AccordionLabel>
          {icon && (
            <IconWrapper>
              <RIcon icon={icon} size="md" />
            </IconWrapper>
          )}
          <div>
            <TruncatedText variant={TypographyVariant.bodyMd2} lineClamp={1}>
              {title}
            </TruncatedText>
            <TruncatedText variant={TypographyVariant.bodySm1} lineClamp={2}>
              {description}
            </TruncatedText>
          </div>
        </AccordionLabel>
      </Accordion.Control>
      <Accordion.Panel>
        <Grid>
          <Column span={6}>
            <Typography variant={TypographyVariant.bodyMd2}>
              {t('manageBenefitFeatures.featureItem.details', {
                defaultValue: 'Details',
              })}
            </Typography>
            <Input
              name={t('manageBenefitFeatures.featureItem.title', {
                defaultValue: 'Title',
              })}
              value={title ?? ''}
              variant={InputVariant.Text}
              onChange={(event) =>
                handleFieldChange('title', event.target.value)
              }
            />
            <Input
              name={t('manageBenefitFeatures.featureItem.description', {
                defaultValue: 'Description',
              })}
              value={description ?? ''}
              variant={InputVariant.TextArea}
              onChange={(event) =>
                handleFieldChange('description', event.target.value)
              }
            />
          </Column>
          <Column span={6}>
            <Typography variant={TypographyVariant.bodyMd2}>
              {t('manageBenefitFeatures.featureItem.display', {
                defaultValue: 'Display',
              })}
            </Typography>
            <IconSelect
              label={t('manageBenefitFeatures.featureItem.icon', {
                defaultValue: 'Icon',
              })}
              value={icon}
              onChange={(value) => handleFieldChange('icon', value)}
              clearable={false}
            />
            <Select
              label={t('manageBenefitFeatures.featureItem.category', {
                defaultValue: 'Category',
              })}
              data={featureCategoryOptions}
              value={category}
              onChange={(value) => handleFieldChange('category', value)}
            />
            {showRankField && (
              <Input
                name={t('manageBenefitFeatures.featureItem.rank', {
                  defaultValue: 'Rank',
                })}
                value={rank}
                onChange={(event) =>
                  handleFieldChange('rank', event.target.value)
                }
                type="number"
              />
            )}
          </Column>
        </Grid>
      </Accordion.Panel>
    </Accordion.Item>
  );
};

export default FeatureItem;

const AccordionLabel = styled.div`
  ${({ theme }) => css`
    display: flex;
    gap: ${theme.spacing(2)};
    align-items: center;
  `}
`;

const IconWrapper = styled.div`
  flex-shrink: 0;
`;

const Column = styled(Grid.Col)`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
  `}
`;
